/**
 * The last thing worth saying out loud, and the one wrinkle that makes a live region actually speak.
 *
 * A live region announces when its TEXT CHANGES. Two identical messages in a row — "Przeniesiono na
 * pozycję 3 z 5" twice, because the trainer moved a block down and then back up — are one change,
 * so the second is silent. The second identical message is therefore stored with a TRAILING SPACE:
 * invisible on screen, a different string to the accessibility tree, and it re-announces. The
 * alternative — a counter rendered beside the words — would be read out as part of the sentence.
 *
 * Promoted out of `pages/plan-builder/lib/useDayDock.ts` on its second consumer: the week canvas
 * needs the same region for the session drag, and two copies of this trick would have drifted the
 * first time one of them was "simplified".
 */

import { useCallback, useState } from 'react';

export interface Announcer {
  /** Render into an always-mounted live region. Empty when there is nothing to say. */
  readonly message: string;
  /** STABLE across renders — see the hook. */
  readonly announce: (message: string) => void;
}

export function useAnnouncer(): Announcer {
  const [message, setMessage] = useState('');
  /**
   * `useCallback` with the React Compiler on, and deliberately: the identity is part of this hook's
   * CONTRACT rather than an optimisation. Callers put it in an effect's dependency list — "say what
   * just opened, once" — and an announcer whose function was rebuilt per render would say it again
   * on every keystroke. The compiler would very likely memoise this anyway; a caller cannot read
   * that off the code, and a silent regression here is a message repeated into a screen reader.
   */
  const announce = useCallback((next: string) => {
    setMessage((current) => (current === next ? `${next} ` : next));
  }, []);

  return { message, announce };
}
